import React, { useState } from "react";
import { IoIosArrowBack } from "react-icons/io";
import { IoIosArrowForward } from "react-icons/io";

import { PaziaBox } from "components/atoms";
import { monthNames } from "components/molecules/MonthCalendar";

interface TokpCalendarProps {
  startDate: Date;
  endDate: Date;
  rangesLabels: CalendarRangeLabel[];
  defaultActiveMonth?: { month: number; year: number };
}

interface TokpCalendarLabelProps {
  day: Date;
  rangesLabels: CalendarRangeLabel[];
}

const TokpCalendarLabel = ({ day, rangesLabels }: TokpCalendarLabelProps) => {
  const label = rangesLabels.find((x) => {
    const start = new Date(x.startDate);
    const end = new Date(x.endDate);
    const d = new Date(day);
    return start.setHours(0, 0, 0, 0) <= d.setHours(0, 0, 0, 0) && end.setHours(0, 0, 0, 0) >= d.setHours(0, 0, 0, 0);
  });

  const colorClass =
    label?.labelColor === "slate"
      ? "text-slate-500 dark:text-slate-200"
      : label?.labelColor === "green"
        ? "text-green-500"
        : "text-red-500";

  return (
    <div className={`flex w-full grow items-end justify-end text-right text-[0.625rem] md:text-xs ${colorClass}`}>
      {label?.label}
    </div>
  );
};

export const TokpCalendar = ({ startDate, endDate, rangesLabels, defaultActiveMonth }: TokpCalendarProps) => {
  const startMonth = { month: startDate.getMonth(), year: startDate.getFullYear() };
  const endMonth = { month: endDate.getMonth(), year: endDate.getFullYear() };

  const listOfMonthBetweenStartAndEnd: { month: number; year: number }[] = [];
  for (let i = startMonth.year; i <= endMonth.year; i++) {
    for (let j = 0; j <= 11; j++) {
      if (i === startMonth.year && j < startMonth.month) continue;
      if (i === endMonth.year && j > endMonth.month) continue;
      listOfMonthBetweenStartAndEnd.push({ month: j, year: i });
    }
  }

  const moveNextMonth = () => {
    const index = listOfMonthBetweenStartAndEnd.findIndex(
      (x) => x.month === activeMonth.month && x.year === activeMonth.year,
    );
    if (index < listOfMonthBetweenStartAndEnd.length - 1) {
      setActiveMonth(listOfMonthBetweenStartAndEnd[index + 1]);
    }
  };

  const movePreviousMonth = () => {
    const index = listOfMonthBetweenStartAndEnd.findIndex(
      (x) => x.month === activeMonth.month && x.year === activeMonth.year,
    );
    if (index > 0) {
      setActiveMonth(listOfMonthBetweenStartAndEnd[index - 1]);
    }
  };

  const [activeMonth, setActiveMonth] = useState(defaultActiveMonth ?? listOfMonthBetweenStartAndEnd[0]);
  return (
    <PaziaBox>
      <div className="flex justify-between pb-4">
        <IoIosArrowBack
          className="cursor-pointer text-slate-500 dark:text-slate-200"
          size={20}
          onClick={movePreviousMonth}
        />
        <span className="text-sm font-semibold uppercase text-gray-600 dark:text-slate-200">
          {monthNames[activeMonth.month]} - {activeMonth.year}
        </span>
        <IoIosArrowForward
          className="cursor-pointer text-slate-500 dark:text-slate-200"
          size={20}
          onClick={moveNextMonth}
        />
      </div>
      <div className="grid grid-cols-7 gap-2 border-t pb-2 pt-4 text-xs font-medium uppercase">
        {["Pon", "Wt", "Śr", "Czw", "Pt", "Sb", "Ndz"].map((day, dayIdx) => (
          <span
            className={`flex items-center justify-center rounded-sm border px-3 py-2 shadow-md ${
              [5, 6].includes(dayIdx) ? "border-blue-500 text-blue-500" : "border-black-500 text-black-500"
            }`}
          >
            {day}
          </span>
        ))}
      </div>
      {listOfMonthBetweenStartAndEnd.map(
        (month) =>
          month.month === activeMonth.month &&
          month.year === activeMonth.year && (
            <TokpCalendarMonth monthIndex={month.month} year={month.year} rangesLabels={rangesLabels} />
          ),
      )}
    </PaziaBox>
  );
};

type DayInWeek = 1 | 2 | 3 | 4 | 5 | 6 | 7;

const getDayNumberInAWeek = (date: Date): DayInWeek => {
  const day = date.getDay();
  return day === 0 ? 7 : (day as DayInWeek);
};

interface TokpCalendarMonthProps {
  monthIndex: number;
  year: number;
  rangesLabels: CalendarRangeLabel[];
}
const TokpCalendarMonth = ({ monthIndex, year, rangesLabels }: TokpCalendarMonthProps) => {
  const firstDay = getDayNumberInAWeek(new Date(year, monthIndex, 1));
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

  const previusMonthNumberOfDays = new Date(year, monthIndex, 0).getDate();
  const previusMonthDaysArray = Array.from(
    { length: firstDay - 1 },
    (_, i) => previusMonthNumberOfDays - firstDay + 2 + i,
  );

  const daysInRestOfFirstWeek = Array.from({ length: 8 - firstDay }, (_, i) => i + 1);
  const numberOfFullWeeks = Math.floor((daysInMonth - daysInRestOfFirstWeek.length) / 7);
  const firstDayOfFullWeeks = daysInRestOfFirstWeek[daysInRestOfFirstWeek.length - 1] + 1;
  const lastDayOfFullWeeks = firstDayOfFullWeeks + 6 + (numberOfFullWeeks - 1) * 7;

  const lastWeekDays = Array.from({ length: daysInMonth - lastDayOfFullWeeks }, (_, i) => lastDayOfFullWeeks + i + 1);
  const restOfDaysInLastWeek = Array.from({ length: 7 - lastWeekDays.length }, (_, i) => 1 + i);

  return (
    <>
      <div className="mb-2 grid grid-cols-7 gap-2 text-sm font-medium">
        {previusMonthDaysArray.map((day) => (
          <span key={day} className="mx-[2px] flex items-center justify-center border px-1 text-gray-400">
            {day}
          </span>
        ))}
        {daysInRestOfFirstWeek.map((day) => (
          <StandardDayCard
            key={day}
            cardNumber={day}
            rangesLabels={rangesLabels}
            day={new Date(year, monthIndex, day)}
          />
        ))}
      </div>
      {Array.from({ length: numberOfFullWeeks }, (_, weekIdx) => (
        <div className="grid grid-cols-7 gap-2 pb-2 text-sm font-medium">
          {Array.from({ length: 7 }, (_, i) => (
            <StandardDayCard
              key={i}
              cardNumber={firstDayOfFullWeeks + i + weekIdx * 7}
              rangesLabels={rangesLabels}
              day={new Date(year, monthIndex, firstDayOfFullWeeks + i + weekIdx * 7)}
            />
          ))}
        </div>
      ))}

      <div className="grid grid-cols-7 gap-2 pb-2 text-sm font-medium">
        {lastWeekDays.map((day) => (
          <StandardDayCard
            key={day}
            cardNumber={day}
            rangesLabels={rangesLabels}
            day={new Date(year, monthIndex, day)}
          />
        ))}
        {restOfDaysInLastWeek.map((day) => (
          <span key={day} className="mx-[2px] flex items-center justify-center border px-1 text-gray-400">
            {day}
          </span>
        ))}
      </div>
    </>
  );
};

export interface CalendarRangeLabel {
  startDate: Date;
  endDate: Date;
  label: string;
  labelColor: "slate" | "green" | "red";
}

interface StanderdDayCardProps {
  cardNumber: number;
  rangesLabels: CalendarRangeLabel[];
  day: Date;
}
const StandardDayCard = ({ cardNumber, rangesLabels, day }: StanderdDayCardProps) => {
  return (
    <div className="mx-[2px] flex h-14 cursor-pointer flex-col border px-1 pt-1 hover:border-green-500 hover:text-green-500">
      <div>{cardNumber}</div>
      <TokpCalendarLabel rangesLabels={rangesLabels} day={day} />
    </div>
  );
};
